/**
 * Concept Sets Store
 * State management for concept set CRUD operations
 */
import { defineStore } from 'pinia'
import { matchesTerms } from '@/utils/list-filters'
import { ref, computed, watch } from 'vue'
import {
  getAllConceptSets,
  getConceptSetById,
  createConceptSet,
  updateConceptSet,
  deleteConceptSet,
  assignTagToConceptSet,
  unassignTagFromConceptSet,
} from '@/services/concept-set.service'
import type {
  ConceptSet,
  ConceptSetListItem,
  ConceptSetItem,
  ConceptAddFlags,
  ComparisonResultItem,
  ConceptSetExpression,
} from '@/models/concept-set.types'
import type { Concept } from '@/models/concept-set.types'
import type { Version } from '@/components/versions/types'
import type { Tag } from '@/models/cohort.types'
import { getUserString, isDateInRange, type DateRange } from '@/utils/list-filters'
import { conceptToConceptSetItem, conceptSetItemToExpressionItem } from '@/utils/api-mappers'
import { diffConceptLists } from '@/utils/concept-compare'
import {
  getVersion as getVersionAPI,
  type ConceptSetVersionedAsset,
} from '@/services/concept-set-versions.service'
import {
  getRecommendedConcepts,
  getConceptRecordCounts,
  compareConceptSets,
  resolveConceptSetExpression,
  getMappedSourceCodes,
} from '@/services/concept-search.service'
import { useWebAPIStore } from '@/stores/webapi'
import { logger } from '@/utils/logger'
import { debounce } from '@/utils/debounce'
import { getSourceKey } from '@/config/webapi'
import { useI18n } from '@/composables/useI18n'

export interface ConceptSetFilterState {
  searchQuery: string
  author: string
  selectedTags: string[]
  createdDateRange: DateRange
  modifiedDateRange: DateRange
}

/** Which projection of the two concept sets the Compare view diffs. */
export type ComparisonMode = 'expression' | 'included' | 'source'

export const useConceptSetsStore = defineStore('concept-sets', () => {
  const { tv } = useI18n()
  // ============================================================================
  // State
  // ============================================================================

  const conceptSets = ref<ConceptSetListItem[]>([])
  const currentSet = ref<ConceptSet | null>(null)
  const loading = ref<boolean>(false)
  const error = ref<string | null>(null)
  let fetchAllInFlight: Promise<void> | null = null
  // getConceptSetById takes no abort signal, so stale responses are dropped by
  // sequence number instead.
  let fetchOneRequestId = 0
  const filters = ref<ConceptSetFilterState>({
    searchQuery: '',
    author: '',
    selectedTags: [],
    createdDateRange: {},
    modifiedDateRange: {},
  })

  // Backward-compatible alias: existing callers/tests read & write filterTerm.
  const filterTerm = computed<string>({
    get: () => filters.value.searchQuery,
    set: (v: string) => {
      filters.value.searchQuery = v
    },
  })
  const editorOpen = ref<boolean>(false)

  // Version preview state (T017)
  const previewVersion = ref<Version | null>(null)
  const isDirty = ref<boolean>(false)

  const recommendedConcepts = ref<Concept[]>([])
  const loadingRecommended = ref<boolean>(false)
  const isRecommendedAvailable = ref<boolean>(true)
  const recommendedError = ref<string | null>(null)

  const comparison = ref<ComparisonResultItem[]>([])
  const comparisonOtherSet = ref<ConceptSet | null>(null)
  const loadingComparison = ref<boolean>(false)
  const comparisonError = ref<string | null>(null)
  const comparisonMode = ref<ComparisonMode>('included')
  // Per-mode result cache, valid for the current (currentSet, other set) pair.
  // Cleared when the other set changes or a fresh compare is requested.
  let comparisonCache: Partial<Record<ComparisonMode, ComparisonResultItem[]>> = {}
  let comparisonCacheOtherId: number | string | null = null
  function clearComparisonCache(): void {
    comparisonCache = {}
    comparisonCacheOtherId = null
  }

  const includedItems = ref<Concept[]>([])
  const includedLoading = ref<boolean>(false)
  const includedError = ref<string | null>(null)
  const includedFetchedAt = ref<number | null>(null)
  let includedAbortCtrl: AbortController | null = null

  // Mapped source codes (the "Source Codes" tab) — the non-standard codes that
  // map to the resolved/included standard concepts.
  const sourceCodeItems = ref<Concept[]>([])
  const sourceCodeLoading = ref<boolean>(false)
  const sourceCodeError = ref<string | null>(null)
  const sourceCodeFetchedAt = ref<number | null>(null)
  let sourceCodeAbortCtrl: AbortController | null = null

  // ============================================================================
  // Getters
  // ============================================================================

  const availableTags = computed(() => {
    const tagSet = new Set<string>()
    for (const cs of conceptSets.value) {
      for (const tag of cs.tags ?? []) {
        if (tag?.name) tagSet.add(tag.name)
      }
    }
    return Array.from(tagSet).sort()
  })

  const availableAuthors = computed(() => {
    const authorSet = new Set<string>()
    for (const set of conceptSets.value) {
      const author = getUserString(set.createdBy)
      if (author) authorSet.add(author)
    }
    return Array.from(authorSet).sort()
  })

  const activeFilterCount = computed(() => {
    let count = 0
    if (filters.value.searchQuery) count++
    if (filters.value.author) count++
    if (filters.value.createdDateRange.from || filters.value.createdDateRange.to) count++
    if (filters.value.modifiedDateRange.from || filters.value.modifiedDateRange.to) count++
    count += filters.value.selectedTags.length > 0 ? 1 : 0
    return count
  })

  const filteredSets = computed(() => {
    const query = filters.value.searchQuery.toLowerCase().trim()
    const hasSearch = query.length > 0
    const hasAuthor = filters.value.author.length > 0
    const authorQuery = hasAuthor ? filters.value.author.toLowerCase() : ''
    const hasCreated = !!(
      filters.value.createdDateRange.from || filters.value.createdDateRange.to
    )
    const hasModified = !!(
      filters.value.modifiedDateRange.from || filters.value.modifiedDateRange.to
    )
    const hasTags = filters.value.selectedTags.length > 0

    if (!hasSearch && !hasAuthor && !hasCreated && !hasModified && !hasTags) {
      return conceptSets.value
    }

    return conceptSets.value.filter(set => {
      if (hasSearch && !matchesTerms([set.name], query)) return false
      if (hasAuthor && !getUserString(set.createdBy).includes(authorQuery)) return false
      if (hasCreated && !isDateInRange(set.createdDate, filters.value.createdDateRange)) return false
      if (hasModified && !isDateInRange(set.modifiedDate, filters.value.modifiedDateRange)) return false
      if (hasTags) {
        const names = new Set((set.tags ?? []).map(t => t.name))
        if (!filters.value.selectedTags.some(name => names.has(name))) return false
      }
      return true
    })
  })

  const isEmpty = computed(() => conceptSets.value.length === 0)

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Fetch all concept sets
   */
  async function fetchAll(): Promise<void> {
    // Concurrent callers share the in-flight request, so awaiting fetchAll()
    // always resolves against the response rather than the previous list.
    if (fetchAllInFlight) {
      return fetchAllInFlight
    }

    loading.value = true
    error.value = null

    fetchAllInFlight = (async () => {
      try {
        conceptSets.value = await getAllConceptSets()
      } catch (err) {
        error.value =
          err instanceof Error
            ? err.message
            : tv('conceptSets.loadListFailed')
        logger.error('ConceptSetsStore', 'Fetch concept sets error', err)
        conceptSets.value = []
      } finally {
        fetchAllInFlight = null
        loading.value = false
      }
    })()

    return fetchAllInFlight
  }

  /**
   * Fetch a single concept set by ID with full details
   */
  async function fetchOne(id: number | string) {
    const requestId = ++fetchOneRequestId

    loading.value = true
    error.value = null

    try {
      const set = await getConceptSetById(id, { rethrow: true })
      if (requestId !== fetchOneRequestId) return
      if (set) {
        currentSet.value = set
      } else {
        error.value = tv('conceptSets.notFound')
        currentSet.value = null
      }
    } catch (err) {
      if (requestId !== fetchOneRequestId) return
      error.value =
        err instanceof Error
          ? err.message
          : tv('conceptSets.loadFailed')
      logger.error('ConceptSetsStore', 'Fetch concept set error', err)
      currentSet.value = null
    } finally {
      if (requestId === fetchOneRequestId) {
        loading.value = false
      }
    }
  }

  /**
   * Create a new concept set
   */
  async function create(
    set: Omit<ConceptSet, 'id' | 'createdDate' | 'createdBy' | 'modifiedDate' | 'modifiedBy'>
  ) {
    loading.value = true
    error.value = null

    try {
      const created = await createConceptSet(set)
      // Refresh the list
      await fetchAll()
      currentSet.value = created
      return created
    } catch (err) {
      error.value =
        err instanceof Error
          ? err.message
          : tv('conceptSets.createFailed')
      logger.error('ConceptSetsStore', 'Create concept set error', err)
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Update an existing concept set
   */
  async function update(set: ConceptSet) {
    loading.value = true
    error.value = null

    try {
      const updated = await updateConceptSet(set)
      // Refresh the list
      await fetchAll()
      currentSet.value = updated
      return updated
    } catch (err) {
      error.value =
        err instanceof Error
          ? err.message
          : tv('conceptSets.updateFailed')
      logger.error('ConceptSetsStore', 'Update concept set error', err)
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Create and open a duplicate of an existing concept set.
   */
  async function copy(id: number | string) {
    loading.value = true
    error.value = null

    try {
      const source = await getConceptSetById(id, { rethrow: true })
      if (!source) {
        error.value = tv('conceptSets.notFound')
        return null
      }

      const existingNames = new Set(conceptSets.value.map(set => set.name))
      const baseName = `${source.name} (copy)`
      let name = baseName
      let copyNumber = 2
      while (existingNames.has(name)) {
        name = `${baseName} ${copyNumber++}`
      }

      const created = await createConceptSet({
        name,
        description: source.description,
        items: source.items.map(item => ({ ...item })),
      })
      await fetchAll()
      currentSet.value = created
      editorOpen.value = true
      return created
    } catch (err) {
      error.value =
        err instanceof Error
          ? err.message
          : tv('conceptSets.copyFailed')
      logger.error('ConceptSetsStore', 'Copy concept set error', err)
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Sync tags for a concept set: assign added tags and unassign removed tags.
   */
  async function syncTags(
    id: number | string,
    oldTags: Tag[],
    newTags: Tag[]
  ): Promise<{ success: boolean; error?: string }> {
    const toAdd = newTags.filter(n => n.id && !oldTags.some(o => o.id === n.id))
    const toRemove = oldTags.filter(o => o.id && !newTags.some(n => n.id === o.id))
    const failures: string[] = []
    for (const tag of toAdd) {
      if (tag.id) {
        const result = await assignTagToConceptSet(id, tag.id)
        if (!result.success) failures.push(result.error ?? tv('conceptSets.tagAssignFailed', { name: tag.name }))
      }
    }
    for (const tag of toRemove) {
      if (tag.id) {
        const result = await unassignTagFromConceptSet(id, tag.id)
        if (!result.success) failures.push(result.error ?? tv('conceptSets.tagUnassignFailed', { name: tag.name }))
      }
    }
    if (currentSet.value?.id === id) {
      currentSet.value = { ...currentSet.value, tags: [...newTags] } as typeof currentSet.value
    }
    if (failures.length > 0) {
      error.value = failures.join('; ')
      return { success: false, error: error.value }
    }
    return { success: true }
  }

  /**
   * Delete a concept set
   */
  async function remove(id: number | string) {
    loading.value = true
    error.value = null

    try {
      const success = await deleteConceptSet(id)
      if (success) {
        // Remove from local list
        conceptSets.value = conceptSets.value.filter(set => set.id !== id)
        if (currentSet.value?.id === id) {
          currentSet.value = null
        }
        return true
      } else {
        error.value = tv('conceptSets.deleteFailed')
        return false
      }
    } catch (err) {
      error.value =
        err instanceof Error
          ? err.message
          : tv('conceptSets.deleteFailed')
      logger.error('ConceptSetsStore', 'Delete concept set error', err)
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Set filter term (debounced)
   */
  const setFilter = debounce((term: string) => {
    filterTerm.value = term
  }, 300)

  function setFilters(next: ConceptSetFilterState) {
    filters.value = { ...next, selectedTags: next.selectedTags ?? [] }
  }

  function clearFilters() {
    filters.value = {
      searchQuery: '',
      author: '',
      selectedTags: [],
      createdDateRange: {},
      modifiedDateRange: {},
    }
  }

  /**
   * Open editor for creating new concept set
   */
  function openCreateEditor(id?: number) {
    currentSet.value = {
      id,
      name: '',
      items: [] as ConceptSetItem[],
    } as ConceptSet
    editorOpen.value = true
  }

  /**
   * Open editor for editing existing concept set
   */
  async function openEditEditor(id: number | string) {
    await fetchOne(id)
    if (currentSet.value) editorOpen.value = true
  }

  /**
   * Open editor on a disposable clone of a cohort-embedded concept set, so
   * editor mutations never reach the cohort until the host applies them.
   */
  function openEmbeddedEditor(set: {
    id: number | string
    name: string
    items?: ConceptSetItem[]
  }) {
    currentSet.value = JSON.parse(
      JSON.stringify({ id: set.id, name: set.name, items: set.items ?? [] })
    ) as ConceptSet
    editorOpen.value = true
  }

  /**
   * Close editor
   */
  function closeEditor() {
    editorOpen.value = false
    currentSet.value = null
    error.value = null
  }

  /**
   * Clear error
   */
  function clearError() {
    error.value = null
  }

  // ============================================================================
  // Phase 5: Concept Set Building Actions
  // ============================================================================

  /**
   * Add a concept to the current concept set
   */
  function addConceptToSet(concept: Concept, flags?: ConceptAddFlags) {
    if (!currentSet.value) {
      error.value = tv('conceptSets.noneSelected')
      return
    }

    const item: ConceptSetItem = conceptToConceptSetItem(concept, flags)

    // One concept may appear more than once with different flags. "Include
    // descendants of X" plus "exclude X" is how a set expresses "X's
    // descendants but not X itself", which ATLAS 2.x supports (it pushes items
    // without deduplicating, and resolution is a DISTINCT union of the
    // includes minus the excludes). Only an exact repeat of the same flag
    // combination is a no-op worth refusing (#226).
    if (currentSet.value.items.some(existing => hasSameFlags(existing, item))) {
      error.value = tv(
        'conceptSets.duplicateItem'
      )
      return
    }

    currentSet.value.items.push(item)
    error.value = null
  }

  // Items reaching the store from an expression payload can carry the flags
  // unset rather than false, so compare them normalized. Otherwise an existing
  // row would read as "different flags" and get a pointless twin.
  function hasSameFlags(a: ConceptSetItem, b: ConceptSetItem): boolean {
    return (
      a.conceptId === b.conceptId &&
      !a.isExcluded === !b.isExcluded &&
      !a.includeDescendants === !b.includeDescendants &&
      !a.includeMapped === !b.includeMapped
    )
  }

  /**
   * Remove a concept from the current concept set
   */
  function removeConceptFromSet(conceptId: number) {
    if (!currentSet.value) {
      error.value = tv('conceptSets.noneSelected')
      return
    }

    currentSet.value.items = currentSet.value.items.filter(item => item.conceptId !== conceptId)
  }

  /**
   * Toggle concept flags (descendants, mapped, exclude)
   *
   * Addresses the first row for the concept. Since a concept can now hold more
   * than one row, prefer `toggleConceptItemFlag` wherever the specific row is
   * known.
   */
  function toggleConceptFlag(
    conceptId: number,
    flag: 'includeDescendants' | 'includeMapped' | 'isExcluded'
  ) {
    if (!currentSet.value) {
      error.value = tv('conceptSets.noneSelected')
      return
    }

    const item = currentSet.value.items.find(item => item.conceptId === conceptId)

    if (item) {
      item[flag] = !item[flag]
    }
  }

  /**
   * Remove one specific row. A concept can appear more than once with
   * different flags, so removing by concept id alone would take its siblings
   * with it (#226).
   */
  function removeConceptItem(target: ConceptSetItem) {
    if (!currentSet.value) {
      error.value = tv('conceptSets.noneSelected')
      return
    }

    const index = indexOfItem(target)
    if (index !== -1) {
      currentSet.value.items.splice(index, 1)
    }
  }

  /**
   * Toggle a flag on one specific row, leaving that concept's other rows
   * alone. Refuses a toggle that would make the row an exact copy of a
   * sibling, which `addConceptToSet` would equally have refused.
   */
  function toggleConceptItemFlag(
    target: ConceptSetItem,
    flag: 'includeDescendants' | 'includeMapped' | 'isExcluded'
  ) {
    if (!currentSet.value) {
      error.value = tv('conceptSets.noneSelected')
      return
    }

    const index = indexOfItem(target)
    if (index === -1) return

    const item = currentSet.value.items[index]
    if (!item) return

    const updated: ConceptSetItem = { ...item, [flag]: !item[flag] }
    if (currentSet.value.items.some((other, i) => i !== index && hasSameFlags(other, updated))) {
      error.value = tv(
        'conceptSets.duplicateItemOptions'
      )
      return
    }

    item[flag] = !item[flag]
    error.value = null
  }

  /**
   * Set all three flags on one specific row at once.
   */
  function setConceptItemFlags(target: ConceptSetItem, flags: ConceptAddFlags) {
    if (!currentSet.value) {
      error.value = tv('conceptSets.noneSelected')
      return
    }

    const item = currentSet.value.items[indexOfItem(target)]
    if (!item) return

    if (flags.isExcluded !== undefined) item.isExcluded = flags.isExcluded
    if (flags.includeDescendants !== undefined) item.includeDescendants = flags.includeDescendants
    if (flags.includeMapped !== undefined) item.includeMapped = flags.includeMapped
  }

  /**
   * Locate a row by object identity, falling back to its concept-and-flags
   * signature for callers holding a copy rather than the stored object.
   */
  function indexOfItem(target: ConceptSetItem): number {
    const items = currentSet.value?.items ?? []
    const byReference = items.indexOf(target)
    if (byReference !== -1) return byReference
    return items.findIndex(item => hasSameFlags(item, target))
  }

  /**
   * Check if a concept is in the current concept set
   */
  function isConceptInSet(conceptId: number): boolean {
    if (!currentSet.value) return false

    return currentSet.value.items.some(item => item.conceptId === conceptId)
  }

  // ============================================================================
  // Version Preview Actions (T018-T020)
  // ============================================================================

  /**
   * Load a specific version for preview
   * Fetches the historical version data and sets it as current with preview flag
   * @param versionNumber - The version number to load
   */
  async function loadVersionPreview(versionNumber: number): Promise<void> {
    if (!currentSet.value?.id) {
      logger.error('ConceptSetsStore', 'Cannot load version preview: no current concept set ID')
      throw new Error('No current concept set ID')
    }

    const conceptSetId = currentSet.value.id
    if (typeof conceptSetId !== 'number') {
      logger.error('ConceptSetsStore', 'Concept set ID must be a number for version preview')
      throw new Error('Concept set ID must be a number')
    }

    try {
      loading.value = true
      const versionedAsset: ConceptSetVersionedAsset = await getVersionAPI(
        conceptSetId,
        versionNumber
      )

      // Set preview version metadata
      previewVersion.value = versionedAsset.versionDTO

      // Replace current concept set with historical data, including the
      // historical items (entityDTO alone has none — see ConceptSetVersionedAsset)
      currentSet.value = { ...versionedAsset.entityDTO, items: versionedAsset.items }

      // Mark as clean (read-only mode, no editing)
      isDirty.value = false

      logger.debug('ConceptSetsStore', `Loaded version ${versionNumber} for preview`)
    } catch (err) {
      error.value = err instanceof Error ? err.message
          : tv('conceptSets.versionLoadFailed', {
              version: versionNumber,
            })
      logger.error('ConceptSetsStore', `Failed to load version ${versionNumber}`, err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Clear preview state and reload current version
   * Returns to normal editing mode
   */
  async function clearPreviewVersion(): Promise<void> {
    const wasPreviewingId = currentSet.value?.id

    // Clear preview state
    previewVersion.value = null

    // Reload current version if we were previewing
    if (wasPreviewingId) {
      await fetchOne(wasPreviewingId)
    }

    logger.debug('ConceptSetsStore', 'Preview cleared, returned to current version')
  }

  /**
   * Save the currently previewed version as the new current version
   * Creates a new version with the historical data
   */
  async function savePreviewAsCurrent(): Promise<boolean> {
    if (!previewVersion.value) {
      logger.error('ConceptSetsStore', 'Cannot save preview: not in preview mode')
      return false
    }

    if (!currentSet.value) {
      logger.error('ConceptSetsStore', 'Cannot save preview: no concept set data')
      return false
    }

    try {
      // Save the current (historical) data as new version
      const result = await update(currentSet.value)

      if (result) {
        // Clear preview state after successful save
        previewVersion.value = null
        isDirty.value = false
        logger.debug('ConceptSetsStore', 'Preview saved as current version')
        return true
      }

      return false
    } catch (err) {
      error.value = err instanceof Error ? err.message
          : tv('conceptSets.saveVersionFailed')
      logger.error('ConceptSetsStore', 'Failed to save preview as current', err)
      return false
    }
  }

  async function loadRecommendedConcepts(sourceKey: string): Promise<void> {
    const seed = (currentSet.value?.items ?? [])
      .filter(item => !item.isExcluded)
      .map(item => item.conceptId)

    if (seed.length === 0) {
      recommendedConcepts.value = []
      isRecommendedAvailable.value = true
      recommendedError.value = null
      return
    }

    loadingRecommended.value = true
    recommendedError.value = null
    // A 501 from one source must not leave the "not available" banner up for
    // every later set or source — re-establish availability on each attempt.
    isRecommendedAvailable.value = true

    try {
      const result = await getRecommendedConcepts(sourceKey, seed)

      if (!result.available) {
        isRecommendedAvailable.value = false
        recommendedConcepts.value = []
        return
      }

      const existingIds = new Set((currentSet.value?.items ?? []).map(item => item.conceptId))
      const candidates = result.concepts.filter(c => !existingIds.has(c.conceptId))

      const ids = candidates.map(c => c.conceptId)
      const counts = await getConceptRecordCounts(sourceKey, ids)
      const enriched = candidates.map(c => {
        const rc = counts.get(c.conceptId)
        if (!rc) return c
        return {
          ...c,
          recordCount: rc.recordCount,
          descendantRecordCount: rc.descendantRecordCount,
          personCount: rc.personCount,
          descendantPersonCount: rc.descendantPersonCount,
        }
      })

      recommendedConcepts.value = enriched
    } catch (err) {
      logger.error('ConceptSetsStore', 'Failed to load recommended concepts', err)
      recommendedError.value = String(err)
      recommendedConcepts.value = []
    } finally {
      loadingRecommended.value = false
    }
  }

  /**
   * Compare the current set against another, projected through `mode`:
   *  - expression: the literal expression items (client-side diff)
   *  - included:   resolved standard concepts (server-side compare endpoint)
   *  - source:     mapped source codes of each set's included concepts
   * Results are cached per mode for the current (other set); a different other
   * set or an explicit clearComparisonCache() invalidates the cache.
   */
  async function loadComparisonForMode(
    sourceKey: string,
    otherSetId: number | string,
    mode: ComparisonMode
  ): Promise<void> {
    comparisonMode.value = mode

    if (!currentSet.value || (currentSet.value.items?.length ?? 0) === 0) {
      comparison.value = []
      comparisonOtherSet.value = null
      comparisonError.value = tv('conceptSets.compare.noneLoaded')
      return
    }
    if (otherSetId === currentSet.value.id) {
      comparison.value = []
      comparisonOtherSet.value = null
      comparisonError.value = tv(
        'conceptSets.compare.sameSet'
      )
      return
    }

    // Invalidate the cache when the other set changes.
    if (comparisonCacheOtherId !== otherSetId) clearComparisonCache()
    const cached = comparisonCache[mode]
    if (cached) {
      comparison.value = cached
      return
    }

    loadingComparison.value = true
    comparisonError.value = null
    try {
      const cs2 =
        comparisonOtherSet.value?.id === otherSetId
          ? comparisonOtherSet.value
          : await getConceptSetById(otherSetId)
      if (!cs2) {
        comparisonError.value = tv('conceptSets.compare.otherNotFound')
        comparison.value = []
        comparisonOtherSet.value = null
        return
      }
      comparisonOtherSet.value = cs2

      let rows: ComparisonResultItem[]
      if (mode === 'expression') {
        rows = diffConceptLists(currentSet.value.items ?? [], cs2.items ?? [])
      } else if (mode === 'included') {
        const expr1: ConceptSetExpression = {
          items: currentSet.value.items.map(conceptSetItemToExpressionItem),
        }
        const expr2: ConceptSetExpression = {
          items: (cs2.items ?? []).map(conceptSetItemToExpressionItem),
        }
        rows = await compareConceptSets(sourceKey, expr1, expr2)
      } else {
        rows = await loadSourceComparisonRows(sourceKey, cs2)
      }

      comparisonCache[mode] = rows
      comparisonCacheOtherId = otherSetId
      comparison.value = rows
    } catch (err) {
      logger.error('ConceptSetsStore', 'Failed to load concept set comparison', err)
      comparisonError.value = String(err)
      comparison.value = []
    } finally {
      loadingComparison.value = false
    }
  }

  /** Back-compat wrapper: included-mode comparison. */
  async function loadComparison(sourceKey: string, otherSetId: number | string): Promise<void> {
    await loadComparisonForMode(sourceKey, otherSetId, 'included')
  }

  /**
   * Source-mode rows: resolve each set's included concepts, fetch the source
   * codes they map to, then diff the two source-code lists.
   */
  async function loadSourceComparisonRows(
    sourceKey: string,
    cs2: ConceptSet
  ): Promise<ComparisonResultItem[]> {
    const expr1: ConceptSetExpression = {
      items: (currentSet.value?.items ?? []).map(conceptSetItemToExpressionItem),
    }
    const expr2: ConceptSetExpression = {
      items: (cs2.items ?? []).map(conceptSetItemToExpressionItem),
    }
    const [inc1, inc2] = await Promise.all([
      resolveConceptSetExpression(sourceKey, expr1),
      resolveConceptSetExpression(sourceKey, expr2),
    ])
    const [src1, src2] = await Promise.all([
      getMappedSourceCodes(
        sourceKey,
        inc1.map(c => c.conceptId)
      ),
      getMappedSourceCodes(
        sourceKey,
        inc2.map(c => c.conceptId)
      ),
    ])
    return diffConceptLists(src1, src2)
  }

  // ============================================================================
  // Included concepts
  // ============================================================================

  async function resolveIncluded(sourceKey?: string): Promise<void> {
    const items = currentSet.value?.items ?? []
    if (items.length === 0) {
      includedAbortCtrl?.abort()
      includedAbortCtrl = null
      includedItems.value = []
      includedError.value = null
      includedLoading.value = false
      return
    }

    const key = sourceKey || useWebAPIStore().getValidVocabularySource() || getSourceKey()
    if (!key) {
      includedError.value = tv('conceptSets.noVocabularySource')
      return
    }

    includedAbortCtrl?.abort()
    const ctrl = new AbortController()
    includedAbortCtrl = ctrl

    includedLoading.value = true
    includedError.value = null

    const expression: ConceptSetExpression = {
      items: items.map(conceptSetItemToExpressionItem),
    }

    try {
      const concepts = await resolveConceptSetExpression(key, expression, ctrl.signal)
      if (ctrl !== includedAbortCtrl) return
      includedItems.value = concepts
      includedFetchedAt.value = Date.now()
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return
      if (ctrl !== includedAbortCtrl) return
      includedError.value =
        err instanceof Error
          ? err.message
          : tv('conceptSets.resolveFailed')
      logger.error('ConceptSetsStore', 'resolveIncluded error', err)
    } finally {
      if (ctrl === includedAbortCtrl) {
        includedLoading.value = false
      }
    }
  }

  function resetIncluded(): void {
    includedAbortCtrl?.abort()
    includedAbortCtrl = null
    includedItems.value = []
    includedLoading.value = false
    includedError.value = null
    includedFetchedAt.value = null
    resetSourceCodes()
  }

  async function resolveSourceCodes(sourceKey?: string): Promise<void> {
    const conceptIds = includedItems.value.map((c) => c.conceptId)
    if (conceptIds.length === 0) {
      sourceCodeAbortCtrl?.abort()
      sourceCodeAbortCtrl = null
      sourceCodeItems.value = []
      sourceCodeError.value = null
      sourceCodeLoading.value = false
      return
    }

    const key = sourceKey || useWebAPIStore().getValidVocabularySource()
    if (!key) {
      sourceCodeError.value = tv('conceptSets.noVocabularySource')
      return
    }

    sourceCodeAbortCtrl?.abort()
    const ctrl = new AbortController()
    sourceCodeAbortCtrl = ctrl

    sourceCodeLoading.value = true
    sourceCodeError.value = null

    try {
      const concepts = await getMappedSourceCodes(key, conceptIds, ctrl.signal)
      if (ctrl !== sourceCodeAbortCtrl) return
      sourceCodeItems.value = concepts
      sourceCodeFetchedAt.value = Date.now()
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return
      if (ctrl !== sourceCodeAbortCtrl) return
      sourceCodeError.value =
        err instanceof Error
          ? err.message
          : tv('conceptSets.resolveSourceCodesFailed')
      logger.error('ConceptSetsStore', 'resolveSourceCodes error', err)
    } finally {
      if (ctrl === sourceCodeAbortCtrl) {
        sourceCodeLoading.value = false
      }
    }
  }

  function resetSourceCodes(): void {
    sourceCodeAbortCtrl?.abort()
    sourceCodeAbortCtrl = null
    sourceCodeItems.value = []
    sourceCodeLoading.value = false
    sourceCodeError.value = null
    sourceCodeFetchedAt.value = null
  }

  // ============================================================================
  // Pythia partial-update entry-point
  // ============================================================================

  /**
   * Merge a partial change into `currentSet` from a pythia agent proposal.
   * Mutates in place so the open editor re-renders; callers should pre-shape
   * `payload.items` into full ConceptSetItem records when present.
   *
   * Returns true when something was applied; false if no editor is open
   * or the payload had no recognised fields.
   */
  function applyProposal(payload: {
    name?: string
    description?: string
    itemsToAdd?: ConceptSetItem[]
    items?: ConceptSetItem[]
  }): boolean {
    if (!currentSet.value) return false
    let applied = false

    if (typeof payload.name === 'string' && payload.name.trim()) {
      currentSet.value.name = payload.name
      applied = true
    }
    if (typeof payload.description === 'string') {
      currentSet.value.description = payload.description
      applied = true
    }
    if (Array.isArray(payload.items)) {
      // Full replace
      currentSet.value.items = payload.items
      applied = true
    } else if (Array.isArray(payload.itemsToAdd) && payload.itemsToAdd.length > 0) {
      // Append-only path: skip duplicates by conceptId
      const existing = new Set(currentSet.value.items.map(it => it.conceptId))
      for (const item of payload.itemsToAdd) {
        if (!existing.has(item.conceptId)) {
          currentSet.value.items.push(item)
          existing.add(item.conceptId)
        }
      }
      applied = true
    }

    if (applied) isDirty.value = true
    return applied
  }

  // ============================================================================
  // Debounced watcher: auto-resolve included list on item edits
  // ============================================================================

  const debouncedResolveIncluded = debounce(() => {
    void resolveIncluded()
  }, 500)

  watch(
    () => currentSet.value?.items,
    (items) => {
      if (!items || items.length === 0) {
        debouncedResolveIncluded.cancel()
        includedAbortCtrl?.abort()
        includedAbortCtrl = null
        includedItems.value = []
        includedError.value = null
        includedLoading.value = false
        includedFetchedAt.value = null
        sourceCodeAbortCtrl?.abort()
        sourceCodeAbortCtrl = null
        sourceCodeItems.value = []
        sourceCodeError.value = null
        sourceCodeLoading.value = false
        sourceCodeFetchedAt.value = null
        return
      }
      debouncedResolveIncluded()
    },
    { deep: true },
  )

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // State
    conceptSets,
    currentSet,
    loading,
    error,
    filterTerm,
    filters,
    availableAuthors,
    availableTags,
    activeFilterCount,
    editorOpen,
    previewVersion,
    isDirty,
    recommendedConcepts,
    loadingRecommended,
    isRecommendedAvailable,
    recommendedError,
    comparison,
    comparisonOtherSet,
    loadingComparison,
    comparisonError,
    comparisonMode,

    // Getters
    filteredSets,
    isEmpty,

    // Actions
    fetchAll,
    fetchOne,
    create,
    update,
    copy,
    remove,
    syncTags,
    setFilter,
    setFilters,
    clearFilters,
    openCreateEditor,
    openEditEditor,
    openEmbeddedEditor,
    closeEditor,
    clearError,

    // Phase 5: Building actions
    addConceptToSet,
    removeConceptFromSet,
    toggleConceptFlag,
    isConceptInSet,
    removeConceptItem,
    toggleConceptItemFlag,
    setConceptItemFlags,

    // Pythia agent partial-update entry-point
    applyProposal,

    // Version preview (T018-T020)
    loadVersionPreview,
    clearPreviewVersion,
    savePreviewAsCurrent,
    loadRecommendedConcepts,
    loadComparison,
    loadComparisonForMode,
    clearComparisonCache,

    // Included concepts
    includedItems,
    includedLoading,
    includedError,
    includedFetchedAt,
    resolveIncluded,
    resetIncluded,

    // Source codes (mapped non-standard codes for included concepts)
    sourceCodeItems,
    sourceCodeLoading,
    sourceCodeError,
    sourceCodeFetchedAt,
    resolveSourceCodes,
    resetSourceCodes,
  }
})
